/**
 * La mécanique commune à ce qui s'ouvre par-dessus la page (Modal, Drawer, et Confirmer à travers
 * la Modale) : Échap ferme, le défilement de la page se verrouille, le focus entre dans la fenêtre
 * et revient d'où il venait, le Tab tourne en rond dedans, le thème de l'endroit se recopie sur
 * l'enveloppe du portail. Le dessin est dans ouverture.css.
 */
import { useEffect, useLayoutEffect, useState, type KeyboardEvent, type RefObject, useRef } from "react";

import { cadreDuTheme, type CadreTheme } from "./portail";

/** 400 passe au-dessus du shell mobile du cockpit (TitleBar et BottomBar sont à 300) ;
 *  100 pour une sous-modale qui ne doit PAS le recouvrir ; 500 pour une confirmation. */
export type ZIndexOuverture = 100 | 200 | 300 | 400 | 500;

export const Z_INDEX_CLASS: Record<ZIndexOuverture, string> = {
  100: "z-[100]",
  200: "z-[200]",
  300: "z-[300]",
  400: "z-[400]",
  500: "z-[500]",
};

/** Échap ferme — sauf si quelqu'un dedans (une liste, un menu) l'a déjà pris (`preventDefault`). */
/** Les fenêtres ouvertes qui écoutent Échap, dans l'ordre d'ouverture : seule celle du dessus se ferme. */
const PILE_ECHAP: symbol[] = [];

export function useFermerAEchap(actif: boolean, onClose: () => void) {
  // Le rappel change à chaque rendu : on le lit dans une ref, pour que la fenêtre garde sa place
  // dans la pile (ré-inscrire à chaque rendu la ferait passer au-dessus de celle qu'elle a ouverte).
  const fermer = useRef(onClose);
  fermer.current = onClose;
  useEffect(() => {
    if (!actif) return;
    const moi = Symbol("fenetre");
    PILE_ECHAP.push(moi);
    function auClavier(e: globalThis.KeyboardEvent) {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (PILE_ECHAP[PILE_ECHAP.length - 1] !== moi) return;
      e.preventDefault();
      fermer.current();
    }
    document.addEventListener("keydown", auClavier);
    return () => {
      document.removeEventListener("keydown", auClavier);
      const i = PILE_ECHAP.lastIndexOf(moi);
      if (i >= 0) PILE_ECHAP.splice(i, 1);
    };
  }, [actif]);
}

/** Le défilement de la page, verrouillé tant que c'est ouvert (sinon, sur iOS, un balayage
 *  fait défiler ce qu'il y a derrière). L'ancienne valeur est rendue à la fermeture. */
export function useVerrouDuDefilement(actif: boolean) {
  useEffect(() => {
    if (!actif) return;
    const avant = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = avant;
    };
  }, [actif]);
}

/** Le focus entre dans la fenêtre à l'ouverture (si rien dedans ne l'a déjà pris — un
 *  `autoFocus`), et revient d'où il venait à la fermeture. */
export function useFocusDansLaFenetre(actif: boolean, fenetre: RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!actif) return;
    const avant = document.activeElement;
    const image = requestAnimationFrame(() => {
      const f = fenetre.current;
      if (f && !f.contains(document.activeElement)) f.focus({ preventScroll: true });
    });
    return () => {
      cancelAnimationFrame(image);
      if (avant instanceof HTMLElement && avant.isConnected) avant.focus({ preventScroll: true });
    };
  }, [actif, fenetre]);
}

/** Le thème de l'endroit où la fenêtre est écrite, lu sur un marqueur caché à cet endroit. */
export function useCadreDuMarqueur(actif: boolean, marqueur: RefObject<HTMLElement | null>): CadreTheme {
  const [cadre, setCadre] = useState<CadreTheme>({ sombre: false });
  useLayoutEffect(() => {
    if (actif) setCadre(cadreDuTheme(marqueur.current));
  }, [actif, marqueur]);
  return cadre;
}

const FOCUSABLES =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Tab et Maj+Tab restent dans la fenêtre. */
export function piegerLeTab(e: KeyboardEvent<HTMLElement>) {
  if (e.key !== "Tab") return;
  const f = e.currentTarget;
  const cibles = Array.from(f.querySelectorAll<HTMLElement>(FOCUSABLES));
  if (cibles.length === 0) {
    e.preventDefault();
    return;
  }
  const premier = cibles[0];
  const dernier = cibles[cibles.length - 1];
  const actif = document.activeElement;
  if (e.shiftKey ? actif === premier || actif === f : actif === dernier) {
    e.preventDefault();
    (e.shiftKey ? dernier : premier).focus();
  }
}
